import { useState } from 'react';
import { Button, Popover } from 'antd';
import { TableOutlined } from '@ant-design/icons';
import styles from './GridLayoutPicker.module.css';

const GRID_SIZE = 4;

interface GridLayout {
  rows: number;
  cols: number;
}

interface GridLayoutPickerProps {
  value?: GridLayout;
  options?: string[];
  onChange?: (layout: GridLayout) => void;
  disabled?: boolean;
}

export function GridLayoutPicker({
  value = { rows: 1, cols: 1 },
  options,
  onChange,
  disabled,
}: GridLayoutPickerProps) {
  const allowedSet = options
    ? new Set(options.map((o) => o.toLowerCase()))
    : null;
  const [open, setOpen] = useState(false);
  const [hoverCell, setHoverCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  const selectedCell = {
    row: value.rows - 1,
    col: value.cols - 1,
  };

  const isCellAllowed = (row: number, col: number): boolean => {
    if (!allowedSet) return true;
    return allowedSet.has(`${row + 1}x${col + 1}`);
  };

  const isHighlighted = (row: number, col: number): boolean => {
    const activeCell = hoverCell ?? selectedCell;
    return row <= activeCell.row && col <= activeCell.col;
  };

  const handleCellClick = (row: number, col: number) => {
    if (!isCellAllowed(row, col)) return;
    onChange?.({ rows: row + 1, cols: col + 1 });
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) setHoverCell(null);
  };

  const label = hoverCell
    ? `${hoverCell.row + 1} × ${hoverCell.col + 1}`
    : `${value.rows} × ${value.cols}`;

  const gridContent = (
    <div className={styles.popoverContent}>
      <div className={styles.grid} onMouseLeave={() => setHoverCell(null)}>
        {Array.from({ length: GRID_SIZE }).map((__, row) =>
          Array.from({ length: GRID_SIZE }).map((___, col) => {
            const allowed = isCellAllowed(row, col);
            let cellClass = styles.cell;
            if (!allowed) cellClass = styles.cellDisabled;
            else if (isHighlighted(row, col))
              cellClass = styles.cellHighlighted;

            return (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${row}-${col}`}
                className={cellClass}
                onMouseEnter={() => allowed && setHoverCell({ row, col })}
                onClick={() => handleCellClick(row, col)}
              />
            );
          })
        )}
      </div>
      <span className={styles.label}>{label}</span>
    </div>
  );

  return (
    <Popover
      placement="bottom"
      content={gridContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      arrow={false}
    >
      <Button icon={<TableOutlined />} disabled={disabled}>
        {value.rows} × {value.cols}
      </Button>
    </Popover>
  );
}
